import { Bell, Search, Settings, User, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { GlobalAlertNotification } from "@/components/common/GlobalAlertNotification";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface AppHeaderProps {
  onLoginClick: () => void;
  currentUser: any;
  onLogout: () => void;
}

export function AppHeader({ onLoginClick, currentUser, onLogout }: AppHeaderProps) {
  return (
    <header className="h-14 sm:h-16 bg-surface border-b border-card-border flex items-center px-3 sm:px-4 md:px-6 gap-2 sm:gap-4 shadow-sm">
      <SidebarTrigger className="lg:hidden flex-shrink-0" />
      
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-primary rounded-lg flex items-center justify-center flex-shrink-0">
          <span className="text-primary-foreground font-bold text-xs sm:text-sm">KKG</span>
        </div>
        <div className="hidden sm:block min-w-0">
          <h1 className="font-semibold text-foreground truncate">KKG Electricals</h1>
          <p className="text-xs text-muted-foreground">Management System</p>
        </div>
      </div>

      <div className="flex-1 max-w-xs sm:max-w-md mx-2 sm:mx-4 min-w-0">
        <div className="relative">
          <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
          <Input 
            placeholder="Search..." 
            className="pl-7 sm:pl-10 pr-2 sm:pr-3 bg-surface-variant border-0 focus:ring-2 focus:ring-primary/20 text-sm sm:text-base h-8 sm:h-10"
          />
        </div>
      </div>

      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
        <GlobalAlertNotification />

        {currentUser ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1 sm:gap-2 h-8 sm:h-9 px-1 sm:px-2">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />
                </div>
                <div className="hidden sm:block text-left min-w-0">
                  <p className="text-sm font-medium truncate">{currentUser.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{currentUser.role}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 sm:w-56">
              <DropdownMenuItem>
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button onClick={onLoginClick} variant="default" size="sm" className="h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm">
            <span className="hidden xs:inline">Login</span>
            <span className="xs:hidden">Login</span>
          </Button>
        )}
      </div>
    </header>
  );
}